// Notification rule duration fields are shown to and entered by the user in
// minutes, but the API expects epochs (1 epoch = 30 s) for `*_epochs_gte`
// fields and raw seconds for Device Offline's `time_since_last_seen_gte`.
// These helpers convert at the form/API boundary. Outbound conversions round
// so fractional minutes never produce fractional epochs/seconds.

export const minutesToEpochs = (minutes: number) => Math.round(minutes * 2);
export const epochsToMinutes = (epochs: number) => epochs / 2;
export const minutesToSeconds = (minutes: number) => Math.round(minutes * 60);
export const secondsToMinutes = (seconds: number) => seconds / 60;

// Walks a (possibly nested) conditions object and applies `convert` to every
// numeric field whose key ends in `_epochs_gte` — exactly the epoch-based time
// fields. Non-time fields (e.g. `movement_mean_gte`, `sound_level_gte`) and
// `null`/`undefined` values are passed through untouched.
const mapEpochFields = <T>(
  conditions: T,
  convert: (value: number) => number,
): T => {
  if (!conditions || typeof conditions !== "object") {
    return conditions;
  }

  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(conditions)) {
    if (value !== null && typeof value === "object") {
      result[key] = mapEpochFields(value, convert);
    } else if (key.endsWith("_epochs_gte") && typeof value === "number") {
      result[key] = convert(value);
    } else {
      result[key] = value;
    }
  }
  return result as T;
};

// Minutes (form) -> epochs (API), for the `*_epochs_gte` fields.
export const epochConditionsToApi = <T>(conditions: T): T =>
  mapEpochFields(conditions, minutesToEpochs);

// Epochs (API) -> minutes (form), for the `*_epochs_gte` fields.
export const epochConditionsFromApi = <T>(conditions: T): T =>
  mapEpochFields(conditions, epochsToMinutes);
